import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Target, Sliders, Plane, Laptop, ArrowRight, Compass, Lock } from "lucide-react";
import { getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { ROLES_PREMISSAS_FATURAMENTO } from "../../constants/premissasFaturamento";
import { ROLES_MAO_DE_OBRA } from "../../constants/maoDeObra";
import { ROLES_PREMISSAS_VIAGEM } from "../../constants/premissasViagem";
import { ROLES_PREMISSAS_EQUIPAMENTOS } from "../../constants/premissasEquipamentos";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
});

// As 4 Premissas, ao contrário do fluxo de Custos/Despesas, NÃO são passos sequenciais — cada
// uma é um parâmetro independente que alimenta um módulo diferente (ou nenhum, no caso de
// Equipamentos). Também variam muito mais em quem pode acessar: Faturamento e Mão de Obra são
// executivas (ROLES restritas), Viagem é liberada para qualquer perfil da Gestão Orçamentária
// (mas só admin edita), e Equipamentos volta a ser restrita. Por isso o texto de cada card foca
// em "o que essa premissa alimenta", não em "próximo passo".
const PASSOS = [
  {
    numero: 1,
    icon: Target,
    titulo: "Premissas de Faturamento",
    path: "/gestao-orcamentaria/premissas-faturamento",
    roles: ROLES_PREMISSAS_FATURAMENTO,
    resumo: "Defina vendas, pipeline, meta de faturamento, backlog e alíquotas/margens projetadas — visão executiva, não é por departamento.",
    pontos: [
      "Pipeline, Vendas Totais e Meta de Faturamento Bruto são editáveis; Novas Vendas e os totais líquidos são sempre calculados a partir deles.",
      "Mostra o ano corrente lado a lado com o ano anterior, com variação percentual, sem precisar trocar o seletor de ano.",
      "Independente das demais: alimenta a visão executiva de vendas/margem, sem relação com Mão de Obra, Viagens ou Equipamentos.",
    ],
  },
  {
    numero: 2,
    icon: Sliders,
    titulo: "Premissas · Mão de Obra",
    path: "/gestao-orcamentaria/premissas-mao-de-obra",
    roles: ROLES_MAO_DE_OBRA,
    resumo: "Configure os percentuais de Encargos e os valores de Benefícios usados no custo de todo colaborador, por regime (CLT/Estágio/PJ).",
    pontos: [
      "Encargos e Benefícios são globais por regime; só \"Incluir no Total\" varia por departamento (ex: FGTS Multa entra no custo total de uns e não de outros) — use o seletor de departamento no menu lateral.",
      "Alimenta o cálculo de custo de Custo Mão de Obra (Operação) e Despesas Mão de Obra (demais departamentos) — mude aqui antes de conferir lá.",
      "Mesmo perfil de acesso de Custo/Despesas Mão de Obra, já que também expõe parâmetros usados no salário de todo colaborador.",
    ],
  },
  {
    numero: 3,
    icon: Plane,
    titulo: "Premissas · Viagens",
    path: "/gestao-orcamentaria/premissas-viagem",
    roles: ROLES_PREMISSAS_VIAGEM,
    resumo: "Consulte o ticket médio de viagem por projeto (Operação) e o ticket único \"Backoffice\" (demais departamentos).",
    pontos: [
      "Ticket por Projeto alimenta \"Lançar Viagem\" em Custos Gerais por Projeto; o ticket Backoffice alimenta o mesmo atalho em Despesas Gerais.",
      "Diferente das outras 3 Premissas: liberada para consulta a qualquer perfil com acesso à Gestão Orçamentária — só o admin edita os valores.",
      "Editar aqui muda o valor sugerido em novos lançamentos; não recalcula lançamentos já feitos.",
    ],
  },
  {
    numero: 4,
    icon: Laptop,
    titulo: "Premissas · Equipamentos e Materiais",
    path: "/gestao-orcamentaria/premissas-equipamentos",
    roles: ROLES_PREMISSAS_EQUIPAMENTOS,
    resumo: "Ticket médio único e global de equipamentos e materiais (Fone, Mouse, Teclado, Notebook, Outros).",
    pontos: [
      "Lista fixa de 5 itens — não dá para criar nem remover categoria aqui.",
      "Só o admin edita os valores; os demais perfis com acesso apenas visualizam.",
      "Ainda não está vinculada a nenhum lançamento de custo/despesa — é uma premissa isolada, fora de escopo por ora.",
    ],
  },
];

export default function BoasVindasPremissasView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const role = getRoleFromToken();

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Boas-vindas · Premissas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
              cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px",
              cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24, width: "100%", maxWidth: 860, margin: "0 auto" }}>
          {/* Hero */}
          <section style={{
            display: "flex", alignItems: "flex-start", gap: 16,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "22px 24px",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 46, height: 46, borderRadius: 12, background: `${C.brand}1F`, color: C.brand,
            }}>
              <Compass size={24} />
            </div>
            <div>
              <h1 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: C.text }}>Bem-vindo às Premissas</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                Diferente do fluxo de Custos e de Despesas, as 4 Premissas abaixo não são passos
                sequenciais: cada uma é um parâmetro independente que alimenta um módulo diferente
                (Faturamento → visão executiva de vendas; Mão de Obra → Custo/Despesas Mão de Obra;
                Viagens → "Lançar Viagem" em Custos Gerais por Projeto e Despesas Gerais). O acesso
                também varia bastante entre elas — algumas são restritas à Executiva, Viagens é aberta
                a todo mundo (mas só o admin edita), e Equipamentos ainda não alimenta nenhum lançamento.
                Configure cada uma antes de usar o módulo correspondente.
              </p>
            </div>
          </section>

          {/* Cards das Premissas */}
          <section style={{ display: "flex", flexDirection: "column" }}>
            {PASSOS.map((passo, idx) => {
              const Icon = passo.icon;
              const ultimo = idx === PASSOS.length - 1;
              const temAcesso = passo.roles.includes(role);
              return (
                <div key={passo.numero} style={{ display: "flex", gap: 18 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 36, flexShrink: 0 }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      width: 36, height: 36, borderRadius: "50%", background: C.brand, color: "#fff",
                      fontSize: 15, fontWeight: 800,
                    }}>
                      {passo.numero}
                    </div>
                    {!ultimo && <div style={{ flex: 1, width: 2, background: C.border, marginTop: 6, marginBottom: 6, minHeight: 24 }} />}
                  </div>

                  <div style={{ flex: 1, paddingBottom: ultimo ? 0 : 22, minWidth: 0 }}>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Icon size={18} color={C.brand} />
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>{passo.titulo}</h2>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{passo.resumo}</p>
                      <ul style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 4 }}>
                        {passo.pontos.map((ponto, i) => (
                          <li key={i} style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{ponto}</li>
                        ))}
                      </ul>
                      {temAcesso ? (
                        <button
                          onClick={() => navigate(passo.path)}
                          style={{
                            alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6,
                            background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 8,
                            padding: "8px 14px", color: C.brand, fontSize: 12.5, fontWeight: 700,
                            cursor: "pointer", fontFamily: "inherit", marginTop: 4,
                          }}
                        >
                          Ir para {passo.titulo} <ArrowRight size={13} />
                        </button>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted, marginTop: 4 }}>
                          <Lock size={12} /> Sem acesso a esta tela com o seu perfil atual
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        </main>
      </div>
    </div>
  );
}
